import { inject } from '@angular/core';
import { ActivityEntityName, JobTemplateQuestionCommandId } from '@jms/model';
import { injectQueryClient } from '@ngneat/query';
import { resolveItemFromRow } from '@workbuddy/entities/shared/items';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { EntityDialogs } from '@workbuddy/ui/v2/blocks/entity.dialog';
import { openJobTemplateQuestionVisibilityDialog } from '../dialogs/question.visibility';
import { Command } from '@workbuddy/ui/model';
import { ToastService } from '@workbuddy/ui/services';
import { lastValueFrom } from 'rxjs';
import { JOB_TEMPLATE_QUESTION_QUERY_KEY, JOB_TEMPLATE_QUESTION_SCOPE, type JobTemplateQuestionBoardParams } from '../job.template.question.model';

/**
 * Delete is unguarded: nothing generates from a job template on a schedule, so a template with no
 * questions is a state the author is allowed to be in.
 */
export function buildJobTemplateQuestionRowCommands(row: unknown, params: JobTemplateQuestionBoardParams | undefined): Command[] {

  if (!params?.writable?.()) return [];

  // The row is parent-shaped: its root `_id` is the job template, so both commands address the question.
  const question = resolveItemFromRow(row, JOB_TEMPLATE_QUESTION_SCOPE);

  const entityData = inject(EntityDataServiceRaw);
  const queryClient = injectQueryClient();
  const confirm = inject(ConfirmServicePro);
  const toast = inject(ToastService);
  const dialogs = inject(EntityDialogs);
  const modals = inject(WorkBuddyDialogService);

  return [
    new Command({
      label: 'Edit',
      iconLeft: 'fa-regular fa-pen',
      action: async () => {
        if (!question?._id) return;
        // Params travel with the open, and carry the row's own id: a custom dialog component is handed
        // `params` and nothing else — never the route's record id — and the template on the bag is
        // where the editor reads the question out of.
        await dialogs.open({ operation: 'edit', entity: ActivityEntityName.JobTemplateQuestions, id: question._id, params: { ...params, questionId: question._id } });
      },
    }),
    new Command({
      label: 'Visibility Rule',
      iconLeft: 'fa-regular fa-eye',
      action: () => {
        if (!question?._id) return;
        openJobTemplateQuestionVisibilityDialog(modals, params, question._id);
      },
    }),
    new Command({
      label: 'Delete',
      iconLeft: 'fa-regular fa-trash',
      action: async () => {
        if (!question?._id) return;
        if (!await confirm.confirm('Delete question', `Delete '${question.name}'? Jobs already generated keep the answers they were created with.`, 'danger')) return;

        try {
          await lastValueFrom(entityData.executeCommand(ActivityEntityName.JobTemplateQuestions, JobTemplateQuestionCommandId.DeleteQuestion, { questionId: question._id }));
        } catch (error: unknown) {
          toast.error('Delete question', (error as { error?: { message?: string } })?.error?.message ?? 'The question could not be deleted.');
          return;
        }

        // The board's rows, then the rest of the screen — same order the item board uses.
        await queryClient.invalidateQueries({ queryKey: JOB_TEMPLATE_QUESTION_QUERY_KEY, refetchType: 'all' });
        params.questionsChanged();
      },
    }),
  ];
}
